import { Text, Tag, VStack } from "@chakra-ui/react";
import { Box, Flex } from "@chakra-ui/layout";
import { Col, Row, Empty } from "antd";
import { VotacaoParticipanteItem } from "core/features/assembleia/typings";
import { useState, useEffect } from "react";
import CardDefault from "ui/components/Cards/CardDefault";
import colors from "core/resources/theme/colors";
import useCheckinParticipante from "core/features/assembleia/hooks/useCheckinParticipante";
import useCheckoutParticipante from "core/features/assembleia/hooks/useCheckoutParticipante";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import { Popover } from "antd";
import { AiFillEye } from "react-icons/ai";

interface ListaParticipantesProps {
  participantes: VotacaoParticipanteItem[] | undefined;
  votacaoId: string;
}

function ListaParticipantes({
  participantes,
  votacaoId,
}: ListaParticipantesProps) {
  return (
    <Flex flexDir="column" gridGap={3}>
      <Box px={6}>
        <Box as={Row} gutter={8}>
          <Box as={Col} xs={11}>
            <Text>Morador</Text>
          </Box>
          <Box as={Col} xs={7}>
            <Text>Peso</Text>
          </Box>
        </Box>
      </Box>

      {participantes?.map((participante) => (
        <ParticipanteItem
          participante={participante}
          votacaoId={votacaoId}
          key={participante.id}
        />
      ))}

      {!participantes?.length ? (
        <Flex
          bg="white"
          justifyContent="center"
          alignItems="center"
          flex={1}
          py="32px"
        >
          <Empty />
        </Flex>
      ) : null}
    </Flex>
  );
}

const ParticipanteItem = ({
  participante,
  votacaoId,
}: {
  participante: VotacaoParticipanteItem;
  votacaoId: string;
}) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [localCheckin, setLocalCheckin] = useState(!!participante.dataCheckin);

  const { mutate: checkIn, isLoading: isLoadingCheckin } =
    useCheckinParticipante({
      pessoaId: participante.pessoa.id,
      unidades: participante.unidades?.map((item) => item.id) ?? [],
      votacaoId,
    });

  const { mutate: checkOut, isLoading: isLoadingCheckout } =
    useCheckoutParticipante({
      pessoaId: participante.pessoa.id,
      votacaoId,
    });

  const _isLoading = isLoadingCheckin || isLoadingCheckout;

  useEffect(() => {
    setLocalCheckin(!!participante.dataCheckin);
  }, [participante.dataCheckin]);

  const everyUnidadeInadimplente = participante.unidades?.every(
    (unidade) => unidade.inadimplente
  );

  return (
    <CardDefault>
      <Flex as={Row} gutter={8} alignItems="center">
        <Flex as={Col} xs={11}>
          {participante.pessoa.nome}
        </Flex>
        <Flex as={Col} xs={10}>
          <Popover
            title="Unidade(s)"
            overlayClassName="antd-popover-hide-arrow"
            overlayStyle={{
              borderRadius: 12,
              zIndex: 2,
              maxWidth: 540,
            }}
            overlayInnerStyle={{
              borderRadius: 12,
              boxShadow: "0 0 6px 2px #87878725",
            }}
            content={
              <VStack flexWrap="wrap" gap="4px" spacing={2}>
                {participante.unidades.map((unidade, key) => (
                  <Tag
                    size="xs"
                    key={key}
                    borderRadius="full"
                    variant="solid"
                    colorScheme={unidade.inadimplente ? "red" : "green"}
                    px="4px"
                    py="4px"
                  >
                    {unidade.numero}
                    {unidade.blocoQuadra
                      ? ` - ${unidade.blocoQuadra.nome}`
                      : null}{" "}
                    (Peso: {unidade.peso})
                    {unidade.inadimplente ? " (INADIMPLENTE)" : ""}
                    {/* <TagCloseButton
                  isDisabled={_isLoading}
                  onClick={() => {
                    checkOut(unidade.id, {
                      onSuccess: () => {
                        showSuccess("Unidade removida com sucesso!");
                      },
                      onError: (err) => {
                        showErrorAxios(err);
                      },
                    });
                    setPopoverVisible(false);
                  }}
                /> */}
                  </Tag>
                ))}
              </VStack>
            }
          >
            <Flex gap="4px" cursor="pointer" alignItems="center">
              {everyUnidadeInadimplente ? (
                <Text
                  fontSize={14}
                  fontWeight="bold"
                  fontFamily="Poppins"
                  color={colors.red}
                >
                  Inadimplente
                </Text>
              ) : (
                <>
                  <Text
                    fontSize={24}
                    fontWeight="bold"
                    fontFamily="Poppins"
                    color={colors.black900}
                  >
                    {participante.peso}
                  </Text>

                  <AiFillEye fontSize="16px" />
                </>
              )}
            </Flex>
          </Popover>
        </Flex>
        <Flex as={Col} xs={3} justifyContent="center">
          {localCheckin ? (
            <OutlineButton
              minW="fit-content"
              lineColor={colors.red500}
              isLoading={_isLoading}
              disabled={_isLoading}
              onClick={() => {
                checkOut(undefined, {
                  onSuccess: () => setLocalCheckin(false),
                });
                setPopoverVisible(false);
              }}
            >
              Remover
            </OutlineButton>
          ) : (
            <Popover
              placement="bottomRight"
              arrowContent={<></>}
              arrowPointAtCenter={false}
              overlayClassName="antd-popover-hide-arrow"
              trigger="click"
              visible={popoverVisible}
              onVisibleChange={setPopoverVisible}
              overlayStyle={{
                borderRadius: 12,
                zIndex: 2,
                width: 210,
              }}
              overlayInnerStyle={{
                borderRadius: 12,
                boxShadow: "0 0 6px 2px #87878725",
              }}
              content={() => (
                <PopoverContent
                  onCheckin={(votoDigital) => {
                    checkIn(votoDigital, {
                      onSuccess: () => setLocalCheckin(true),
                    });
                    setPopoverVisible(false);
                  }}
                />
              )}
            >
              <OutlineButton
                minW="fit-content"
                lineColor={colors.green500}
                isLoading={_isLoading}
                disabled={_isLoading}
              >
                Adicionar
              </OutlineButton>
            </Popover>
          )}
        </Flex>
      </Flex>
    </CardDefault>
  );
};

const PopoverContent = ({
  onCheckin,
}: {
  onCheckin: (votoDigital: boolean) => any;
}) => (
  <Flex flexDir="column">
    <Text>Por onde o morador irá votar?</Text>
    <Flex gridGap={4} mt={4} justifyContent="flex-end">
      <OutlineButton onClick={() => onCheckin(true)}>App</OutlineButton>
      <PrimaryButton onClick={() => onCheckin(false)}>Cédula</PrimaryButton>
    </Flex>
  </Flex>
);

export default ListaParticipantes;
